/**
 * Notification Store — Zustand-based notification system.
 * Replaces ToastProvider context. Components import useNotificationStore directly.
 *
 * Features:
 * - Toast, banner, and dialog notifications
 * - Auto-dismiss with configurable timing per severity
 * - Max 3 visible toasts (overflow queues)
 * - Sentry bridge for warning/error severity
 * - Hover-to-pause (managed by component, store tracks pause state)
 */

import { create } from 'zustand';
import { generateCorrelationId, type ErrorSeverityLevel } from '../constants/errorCodes';

// ─── Types ───────────────────────────────────────────────────────────────────

export type NotificationType = 'success' | 'info' | 'warning' | 'error' | 'loading';

export interface Notification {
  id: string;
  type: NotificationType;
  title?: string;
  message: string;
  code?: string;
  correlationId?: string;
  actionLabel?: string;
  onAction?: () => void;
  autoClose: boolean;
  autoCloseDelay: number;
  timestamp: number;
  dismissed: boolean;
}

export interface BannerState {
  type: 'info' | 'warning' | 'error';
  message: string;
  dismissible: boolean;
  actionLabel?: string;
  onAction?: () => void;
}

export interface DialogState {
  open: boolean;
  title: string;
  message: string;
  code?: string;
  correlationId?: string;
  primaryAction: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
  dismissible: boolean;
}

export interface NotifyOptions {
  type: NotificationType;
  title?: string;
  message: string;
  code?: string;
  correlationId?: string;
  actionLabel?: string;
  onAction?: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

// ─── Auto-dismiss defaults per type ──────────────────────────────────────────

const AUTO_CLOSE_DELAYS: Record<NotificationType, number> = {
  success: 3000,
  info: 5000,
  warning: 8000,
  error: 0, // manual dismiss
  loading: 0, // dismissed programmatically
};

const AUTO_CLOSE_DEFAULTS: Record<NotificationType, boolean> = {
  success: true,
  info: true,
  warning: true,
  error: false,
  loading: false,
};

// ─── Store Interface ─────────────────────────────────────────────────────────

interface NotificationStoreState {
  notifications: Notification[];
  banner: BannerState | null;
  dialog: DialogState | null;
  paused: boolean;
}

interface NotificationStoreActions {
  // Core
  notify: (opts: NotifyOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;

  // Convenience
  success: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
  warn: (message: string, code?: string, correlationId?: string) => void;
  error: (message: string, code?: string, correlationId?: string, actionLabel?: string, onAction?: () => void) => void;
  loading: (message: string) => string;

  // Banner
  showBanner: (banner: BannerState) => void;
  dismissBanner: () => void;

  // Dialog
  showDialog: (dialog: Omit<DialogState, 'open'>) => void;
  dismissDialog: () => void;

  // Pause (hover-to-pause)
  setPaused: (paused: boolean) => void;
}

type NotificationStore = NotificationStoreState & NotificationStoreActions;

// ─── Store ───────────────────────────────────────────────────────────────────

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  banner: null,
  dialog: null,
  paused: false,

  notify: (opts) => {
    const id = `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const autoClose = opts.autoClose ?? AUTO_CLOSE_DEFAULTS[opts.type];
    const autoCloseDelay = opts.autoCloseDelay ?? AUTO_CLOSE_DELAYS[opts.type];

    const notification: Notification = {
      id,
      type: opts.type,
      title: opts.title,
      message: opts.message,
      code: opts.code,
      correlationId: opts.correlationId ?? (opts.type === 'error' || opts.type === 'warning' ? generateCorrelationId() : undefined),
      actionLabel: opts.actionLabel,
      onAction: opts.onAction,
      autoClose,
      autoCloseDelay,
      timestamp: Date.now(),
      dismissed: false,
    };

    set((state) => ({
      notifications: [...state.notifications, notification],
    }));

    // Emit to Sentry for warning/error (Phase 3 wires this up properly)
    if (opts.type === 'warning' || opts.type === 'error') {
      emitToObservability(notification);
    }

    return id;
  },

  dismiss: (id) => {
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, dismissed: true } : n
      ),
    }));
    // Remove from array after exit animation (300ms)
    setTimeout(() => {
      set((state) => ({
        notifications: state.notifications.filter((n) => n.id !== id),
      }));
    }, 300);
  },

  dismissAll: () => {
    set({ notifications: [] });
  },

  success: (message, title) => {
    get().notify({ type: 'success', message, title });
  },

  info: (message, title) => {
    get().notify({ type: 'info', message, title });
  },

  warn: (message, code, correlationId) => {
    get().notify({ type: 'warning', message, code, correlationId });
  },

  error: (message, code, correlationId, actionLabel, onAction) => {
    get().notify({ type: 'error', message, code, correlationId, actionLabel, onAction });
  },

  loading: (message) => {
    return get().notify({ type: 'loading', message });
  },

  showBanner: (banner) => {
    set({ banner });
  },

  dismissBanner: () => {
    set({ banner: null });
  },

  showDialog: (dialog) => {
    set({ dialog: { ...dialog, open: true } });
  },

  dismissDialog: () => {
    set({ dialog: null });
  },

  setPaused: (paused) => {
    set({ paused });
  },
}));

// ─── Observability Bridge ────────────────────────────────────────────────────
// Emits warning/error notifications to Sentry via SentryMonitoring with error code tags.

function emitToObservability(notification: Notification): void {
  try {
    import('../services/sentryMonitoring').then(({ SentryMonitoring }) => {
      import('../services/errorLogger').then(({ ErrorCategory, ErrorSeverity }) => {
        // Set error code tags before capture
        import('@sentry/react').then((Sentry) => {
          if (notification.code) {
            Sentry.setTag('error.code', notification.code);
            // Extract domain from code: SE-AUTH-001 → AUTH
            const domain = notification.code.split('-')[1];
            if (domain) Sentry.setTag('error.domain', domain);
          }
          if (notification.correlationId) {
            Sentry.setTag('correlation.id', notification.correlationId);
          }
          Sentry.setTag('notification.type', notification.type);
        });

        SentryMonitoring.captureError(
          notification.message,
          {
            component: 'NotificationStore',
            route: typeof window !== 'undefined' ? window.location.pathname : undefined,
            additionalData: {
              errorCode: notification.code,
              correlationId: notification.correlationId,
              notificationType: notification.type,
            },
          },
          ErrorCategory.UI,
          notification.type === 'error' ? ErrorSeverity.ERROR : ErrorSeverity.WARNING
        );
      });
    }).catch(() => {
      // Silent — Sentry may not be initialized in dev
    });
  } catch {
    // Guard against SSR or test environments
  }
}

// ─── Selectors ───────────────────────────────────────────────────────────────
// ponytail: selectors that derive arrays must return stable references or
// useSyncExternalStore (Zustand's engine) detects a "new" value every render
// and forces an infinite re-render loop. Cache previous result and only replace
// when the output actually changes (shallow array comparison on ids).

let _prevVisible: Notification[] = [];
/** Get only non-dismissed, visible notifications (max 3) */
export const selectVisibleNotifications = (state: NotificationStoreState): Notification[] => {
  const next = state.notifications.filter((n) => !n.dismissed).slice(-3);
  if (
    next.length === _prevVisible.length &&
    next.every((n, i) => n.id === _prevVisible[i]?.id && n.dismissed === _prevVisible[i]?.dismissed)
  ) {
    return _prevVisible;
  }
  _prevVisible = next;
  return next;
};

let _prevQueued = 0;
/** Get queued notifications waiting to be shown */
export const selectQueuedCount = (state: NotificationStoreState): number => {
  const next = Math.max(0, state.notifications.filter((n) => !n.dismissed).length - 3);
  if (next === _prevQueued) return _prevQueued;
  _prevQueued = next;
  return next;
};
